import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';

/**
 * BOTÓN DE DICTADO genérico (pedido del dueño 2026-10-02, segunda ronda: "déjame también dictarle
 * en inglés o español para que agarre toda la idea y lo ponga ahí"). Graba con MediaRecorder y manda
 * el audio al endpoint que se le indique; el servidor lo transcribe TAL CUAL (Whisper, sin mejorar ni
 * traducir) y el texto se entrega por `onText` para que el cuadro lo reciba. Lo que haga con el texto
 * después (generar un prompt, redactar) es cosa de quien lo use.
 *
 * Tope de 5 minutos. Safari graba audio/mp4; Chrome y Firefox webm/opus.
 */
type Estado = 'idle' | 'recording' | 'processing';
const MAX_SEG = 300;
/** Menos de un segundo grabando es el clic del micrófono, no un dictado: no vale el viaje al servidor (Sentry 2026-10-14). */
const MIN_MS = 1000;
const MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/mpeg'];

function elegirMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIMES) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* */ } }
  return '';
}

export function DictateButton({ endpoint, onText, onError, className, compact = false }: {
  endpoint: string; onText: (text: string) => void; onError?: (msg: string) => void; className?: string;
  /** Solo el ícono (y el reloj mientras graba): para barras de herramientas como la del editor. */
  compact?: boolean;
}) {
  const { t } = useI18n();
  const [estado, setEstado] = useState<Estado>('idle');
  const [seg, setSeg] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const inicioRef = useRef(0);
  const soportado = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && elegirMime() !== null;

  useEffect(() => () => { limpiar(); }, []);
  function limpiar() {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null; recRef.current = null;
  }

  async function empezar() {
    let stream: MediaStream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { onError?.(t('ai.dict.noMic')); return; }
    const mime = elegirMime() || '';
    let rec: MediaRecorder;
    try { rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
    catch { stream.getTracks().forEach((tr) => tr.stop()); onError?.(t('ai.dict.unsupported')); return; }
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => { void procesar(rec.mimeType || mime || 'audio/webm'); };
    recRef.current = rec; streamRef.current = stream;
    rec.start(1000);
    inicioRef.current = Date.now();
    setSeg(0); setEstado('recording');
    timerRef.current = window.setInterval(() => setSeg((s) => { if (s + 1 >= MAX_SEG) parar(); return s + 1; }), 1000);
  }

  function parar() {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') { try { rec.stop(); } catch { /* */ } }
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
  }

  async function procesar(mime: string) {
    setEstado('processing');
    const blob = new Blob(chunksRef.current, { type: mime });
    const duracionMs = Date.now() - inicioRef.current;
    limpiar();
    if (blob.size < 1000 || duracionMs < MIN_MS) { onError?.(t('ai.dict.tooShort')); setEstado('idle'); return; }
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    const ext = /mp4|m4a|aac/.test(mime) ? 'm4a' : /ogg/.test(mime) ? 'ogg' : /mpeg|mp3/.test(mime) ? 'mp3' : 'webm';
    try {
      const r = await api.post<{ text: string }>(endpoint, { base64, mime: mime.split(';')[0], filename: `dictation.${ext}` });
      if (r.text?.trim()) onText(r.text.trim());
      else onError?.(t('ai.dict.failed'));
    } catch (e) {
      const code = e instanceof ApiError ? (e.code ?? e.message) : '';
      // `nothing_heard` (422) = la grabación no tenía voz: se le pide grabar de nuevo, no es un fallo del servidor.
      onError?.(code === 'transcription_not_configured' ? t('ai.dict.notConfigured') : code === 'audio_too_short' ? t('ai.dict.tooShort') : code === 'nothing_heard' ? t('ai.dict.nothingHeard') : t('ai.dict.failed'));
    } finally { setEstado('idle'); }
  }

  if (!soportado) return null;
  const mmss = `${Math.floor(seg / 60)}:${String(seg % 60).padStart(2, '0')}`;
  if (compact) {
    const cls = `ai-dict-compact${estado === 'recording' ? ' rec' : ''}${className ? ` ${className}` : ''}`;
    if (estado === 'recording') return <button type="button" tabIndex={-1} className={cls} onMouseDown={(e) => { e.preventDefault(); parar(); }} title={t('ai.dict.stop')} aria-label={t('ai.dict.stop')}><Square size={13} strokeWidth={2} /> <span className="ai-dict-time">{mmss}</span></button>;
    if (estado === 'processing') return <button type="button" tabIndex={-1} className={cls} disabled aria-label={t('ai.dict.transcribing')}><span className="insp-voice-spinner" /></button>;
    return <button type="button" tabIndex={-1} className={cls} onMouseDown={(e) => { e.preventDefault(); void empezar(); }} title={t('ai.dict.hint')} aria-label={t('ai.dict.hint')}><Mic size={14} strokeWidth={2} /></button>;
  }
  if (estado === 'recording') {
    return <button type="button" className={`btn sm ai-dict rec${className ? ` ${className}` : ''}`} onClick={parar} title={t('ai.dict.stop')}><Square size={13} strokeWidth={2} /> {t('ai.dict.stop')} <span className="ai-dict-time">{mmss}</span></button>;
  }
  if (estado === 'processing') {
    return <button type="button" className={`btn ghost sm ai-dict${className ? ` ${className}` : ''}`} disabled><span className="insp-voice-spinner" /> {t('ai.dict.transcribing')}</button>;
  }
  return <button type="button" className={`btn ghost sm ai-dict${className ? ` ${className}` : ''}`} onClick={() => void empezar()} title={t('ai.dict.hint')}><Mic size={13} strokeWidth={2} /> {t('ai.dict.start')}</button>;
}
